import React, { type FC, useEffect } from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import {
  fetchDeviceProfiles,
  fetchDevicesForProvision,
  fetchTenants,
  SearchDevicesForProvisionAction,
  SetCreateDeviceDialogStateAction,
  SetDevicesActivePageAction,
  SetDevicesPageSizeAction,
  SetEditDeviceDialogStateAction,
  SetGenerateCsvFromGivenSerialDialogStateAction,
  SetImportDevicesDialogStateAction,
  SelectedTenantChangedAction
} from '../../../redux/admin/admin-device-provisions-actions'
import { paginateList } from '../../../utils/utils'
import Fab from '@mui/material/Fab'
import Publish from '@mui/icons-material/Publish'
import GetApp from '@mui/icons-material/GetApp'
import ContentAdd from '@mui/icons-material/Add'
import Build from '@mui/icons-material/Build'
import { CreateDeviceForProvisionDialog } from './CreateDeviceForProvisionDialog'
import { EditDeviceForProvisionDialog } from './EditDeviceForProvisionDialog'
import { ImportDevicesForProvisionDialog } from './ImportDevicesForProvisionDialog'
import { GenerateCSVFromGivenSerialDialog } from './GenerateCSVFromGivenSerialDialog'
import { MetersPagination } from '../../common/pagination/MetersPagination'
import { SearchBar } from '../../common/search-bar/SearchBar'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type DeviceForProvision } from '../../../redux/admin/admin-device-provisions-types'
import Fuse from 'fuse.js'
import { type AppDispatch, type RootState } from '../../../redux/store'
import { TenantFilter } from './TenantFilter'

// Tenant selected by default in the filter
const DEFAULT_SELECTED_TENANT = 'Finland'

/**
 * Devices for provision UI component.
 */
export const DevicesForProvision: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const deviceProfilesLoaded = useAppSelector<boolean>((state: RootState) =>
    state.adminDeviceProvisions.deviceProfilesLoaded
  )

  const devicesActivePage = useAppSelector<number>((state: RootState) =>
    state.adminDeviceProvisions.devicesActivePage
  )

  const devicesPageSize = useAppSelector<number>((state: RootState) =>
    state.adminDeviceProvisions.devicesPageSize
  )

  const searchQuery = useAppSelector<string>((state: RootState) =>
    state.adminDeviceProvisions.searchQuery
  )

  const devicesForProvision = useAppSelector<DeviceForProvision[]>((state: RootState) =>
    state.adminDeviceProvisions.devicesForProvision
  )

  const tenantLoadStatus = useAppSelector<string>((state: RootState) => state.adminDeviceProvisions.tenantLoadStatus)
  const selectedTenant = useAppSelector<string>((state: RootState) => state.adminDeviceProvisions.selectedTenant)
  const tenants = useAppSelector((state: RootState): string[] => state.adminDeviceProvisions.tenants)

  const sortedDevices = devicesForProvision.slice().sort((a, b) => a.serialNumber.localeCompare(b.serialNumber))

  const handlePageChange = (pageNumber: number): void => {
    dispatch(SetDevicesActivePageAction({ pageNumber }))
  }

  const changePageSize = (pageSize: number): void => {
    dispatch(SetDevicesPageSizeAction({ pageSize }))
  }

  const ensureLoadingDeviceProfiles = (): void => {
    triggerDeviceProfilesLoadingIfNotLoaded(deviceProfilesLoaded, dispatch)
  }

  const onOpenCreateDeviceForProvisionDialog = (): void => {
    ensureLoadingDeviceProfiles()
    dispatch(SetCreateDeviceDialogStateAction({ isOpen: true }))
  }

  const onOpenImportDevicesForProvisionDialog = (): void => {
    ensureLoadingDeviceProfiles()
    dispatch(SetImportDevicesDialogStateAction({ isOpen: true }))
  }

  const updateSearchQuery = (searchQuery: string): void => {
    dispatch(SearchDevicesForProvisionAction({ searchQuery }))
  }

  useEffect(() => {
    if (tenantLoadStatus === 'idle') {
      dispatch(fetchTenants())
        .catch(err => {
          console.error('fetch tenants error: ', err)
        })
    }
  }, [dispatch, tenantLoadStatus])

  useEffect(() => {
    // Set the default selected tenant if not yet set and tenants are available
    if (selectedTenant !== '' || tenants.length === 0) return

    const defaultTenant = tenants.find((t) => t === DEFAULT_SELECTED_TENANT) ?? tenants[0] ?? ''

    if (defaultTenant !== '') {
      dispatch(SelectedTenantChangedAction(defaultTenant))
    }
  }, [dispatch, selectedTenant, tenants])

  useEffect(() => {
    if (selectedTenant === '') return
    triggerDevicesForProvisionLoading(dispatch)
  }, [dispatch, selectedTenant])

  useEffect(() => {
    triggerDeviceProfilesLoadingIfNotLoaded(deviceProfilesLoaded, dispatch)
  }, [deviceProfilesLoaded, dispatch])

  const filteredDevices = searchDevicesForProvision(sortedDevices, searchQuery)

  const paginatedDevices = paginateList(
    filteredDevices,
    devicesActivePage,
    devicesPageSize
  )

  return (
        <div style={{ marginBottom: '120px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <SearchBar
                    searchValue={searchQuery}
                    onChange={(e) => {
                      updateSearchQuery(e.target.value)
                    }}
                />
              <TenantFilter
                tenants={tenants}
                selectedTenant={selectedTenant}
                onChange={(newValue) => {
                  dispatch(SelectedTenantChangedAction(newValue))
                  dispatch(SetDevicesActivePageAction({ pageNumber: 1 }))
                }}
              />
                <Fab
                    style={{ margin: '12px' }}
                    color="primary"
                    onClick={() => {
                      onOpenImportDevicesForProvisionDialog()
                    }}
                >
                    <Publish data-tip={t('send_csv')}/>
                </Fab>
                <Fab
                    color="primary"
                    style={{ margin: '12px' }}
                    onClick={() => {
                      onOpenCreateDeviceForProvisionDialog()
                    }}
                >
                    <ContentAdd/>
                </Fab>
                <Fab
                    color="primary"
                    style={{ margin: '12px' }}
                    onClick={() => dispatch(SetGenerateCsvFromGivenSerialDialogStateAction({ isOpen: true }))}
                >
                    <GetApp data-tip={t('download_csv')}/>
                </Fab>
            </div>
            <CreateDeviceForProvisionDialog/>
            <EditDeviceForProvisionDialog/>
            <ImportDevicesForProvisionDialog/>
            <GenerateCSVFromGivenSerialDialog/>
            <Table style={{ backgroundColor: 'white' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Serial Number</TableCell>
                        <TableCell>Dev EUI</TableCell>
                        <TableCell>App EUI</TableCell>
                        <TableCell>App Key</TableCell>
                        <TableCell>Profile</TableCell>
                        <TableCell style={{ width: '100px' }}/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedDevices.map(dfp => {
                      return (
                            <TableRow key={dfp.devEUI}>
                                <TableCell>{dfp.serialNumber}</TableCell>
                                <TableCell>{dfp.devEUI}</TableCell>
                                <TableCell>{dfp.appEUI}</TableCell>
                                <TableCell>{dfp.appKey}</TableCell>
                                <TableCell>{dfp.deviceProfileId}</TableCell>
                                <TableCell style={{
                                  width: '100px',
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(2, 1fr)',
                                  justifyContent: 'center',
                                  paddingRight: '50px'
                                }}>
                                    <Fab
                                        color="primary"
                                        style={{ marginLeft: '10px' }}
                                        key={dfp.devEUI}
                                        onClick={() => {
                                          ensureLoadingDeviceProfiles()
                                          dispatch(SetEditDeviceDialogStateAction({ isOpen: true, editedDevice: dfp }))
                                        }}
                                    >
                                        <Build/>
                                    </Fab>
                                </TableCell>
                            </TableRow>
                      )
                    })}
                </TableBody>
            </Table>
            <MetersPagination
                activePage={devicesActivePage}
                itemsCountPerPage={devicesPageSize}
                totalItemsCount={filteredDevices.length}
                onPageChange={handlePageChange.bind(this)}
                onPageSizeChange={(pageSize) => {
                  changePageSize(pageSize)
                }}
            />
        </div>
  )
}

/* Helper Functions */

const fuseOptions = {
  keys: ['deviceSerialNumber', 'devEUI', 'serialNumber'],
  ignoreLocation: true,
  threshold: 0
}

function searchDevicesForProvision(devicesForProvision: DeviceForProvision[], query: string): DeviceForProvision[] {
  if (query.trim() === '') {
    return devicesForProvision
  }

  const ret = new Fuse(devicesForProvision, fuseOptions).search(query)
  return ret.map((r) => r.item)
}

function triggerDevicesForProvisionLoading(dispatch: AppDispatch): void {
  dispatch(fetchDevicesForProvision())
    .catch(err => {
      console.error('fetch devices for provision error: ', err)
    })
}

function triggerDeviceProfilesLoadingIfNotLoaded(deviceProfilesLoaded: boolean, dispatch: AppDispatch): void {
  if (!deviceProfilesLoaded) {
    dispatch(fetchDeviceProfiles())
      .catch(err => {
        console.error('fetch device profiles error: ', err)
      })
  }
}
